import React, { useState, useEffect } from "react";
import axios from "../../../api/axios";
import {
  Grid,
  Paper,
  Typography,
  Checkbox,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  CircularProgress,
  TableContainer,
  Box,
} from "@mui/material";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

export default function VariationApprover() {
  const [variations, setVariations] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);

  // parse JSON safely
  const safeParse = (jsonString) => {
    try {
      return jsonString ? JSON.parse(jsonString) : {};
    } catch {
      return {};
    }
  };

  const fetchPending = async () => {
    try {
      setLoading(true);
      const res = await axios.get("/variations/pending");
      const parsed = (res.data || []).map((v) => ({
        ...v,
        old: safeParse(v.old_data),
        new: safeParse(v.new_data),
      }));
      setVariations(parsed);
    } catch (err) {
      console.error(err.response?.data || err);
      alert("Error fetching variations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPending();
  }, []);

  const handleToggle = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelected(variations.map((v) => v.id));
    } else {
      setSelected([]);
    }
  };

  const handleApprove = async () => {
    if (selected.length === 0) {
      alert("Please select at least one variation to approve.");
      return;
    }

    try {
      setLoading(true);
      await axios.post("/variations/approve", { ids: selected });
      alert("Selected variations approved successfully");
      setSelected([]);
      fetchPending();
    } catch (err) {
      console.error(err.response?.data || err);
      alert("Error approving variations");
    } finally {
      setLoading(false);
    }
  };

  const formatVal = (v) => {
    if (v === null || v === undefined || v === "") return "-";
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
  };

  const renderChangeRows = (oldObj = {}, newObj = {}) => {
    const keys = Array.from(new Set([...Object.keys(oldObj || {}), ...Object.keys(newObj || {})]));
    if (keys.length === 0) return <Typography variant="body2">No changes</Typography>;
    return keys.map((field) => (
      <Box key={field} sx={{ mb: 0.5 }}>
        <Typography component="span" sx={{ fontWeight: 600 }}>
          {field}:
        </Typography>{" "}
        <Typography component="span" sx={{ color: "error.main", ml: 0.5 }}>
          {formatVal(oldObj[field])}
        </Typography>{" "}
        <Typography component="span" sx={{ mx: 0.5 }}>
          →
        </Typography>
        <Typography component="span" sx={{ color: "success.main" }}>
          {formatVal(newObj[field])}
        </Typography>
      </Box>
    ));
  };

  const allSelected = variations.length > 0 && selected.length === variations.length;
  const someSelected = selected.length > 0 && selected.length < variations.length;

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Grid container justifyContent="center" sx={{ p: 3 }}>
        <Grid item xs={12} md={11}>
          <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Pending Variations
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Review changes and approve one or multiple variations.
            </Typography>

            {loading ? (
              <Grid container justifyContent="center" sx={{ p: 4 }}>
                <CircularProgress />
              </Grid>
            ) : variations.length === 0 ? (
              <Typography>No pending variations found.</Typography>
            ) : (
              <>
                {/* TableContainer is required for stickyHeader to align correctly */}
                <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 3 }}>
                  <Table size="small" stickyHeader sx={{ tableLayout: "fixed" }}>
                    {/* colgroup ensures header/body column widths match */}
                    <colgroup>
                      <col style={{ width: 56 }} />
                      <col style={{ width: 120 }} />
                      <col style={{ width: 180 }} />
                      <col />
                    </colgroup>

                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={allSelected}
                            indeterminate={someSelected}
                            onChange={handleSelectAll}
                            inputProps={{ "aria-label": "select all variations" }}
                          />
                        </TableCell>
                        <TableCell>PF Number</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Changes</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {variations.map((v) => (
                        <TableRow key={v.id} hover>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selected.includes(v.id)}
                              onChange={() => handleToggle(v.id)}
                            />
                          </TableCell>
                          <TableCell>{v.pfno}</TableCell>
                          <TableCell>{v.category}</TableCell>
                          <TableCell sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                            {renderChangeRows(v.old, v.new)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Grid container justifyContent="flex-end" sx={{ mt: 2, gap: 1 }}>
                  <Button
                    variant="contained"
                    color="success"
                    disabled={selected.length === 0 || loading}
                    onClick={handleApprove}
                  >
                    Approve Selected ({selected.length})
                  </Button>
                  <Button variant="outlined" onClick={fetchPending} disabled={loading}>
                    Refresh
                  </Button>
                </Grid>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Footer />
    </DashboardLayout>
  );
}
